import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { onAnimationEnd, debounce } from '@theme/utilities';
import { getBundleStore } from '@theme/pdp-bundle-store';
import { formatMoney, convertMoneyToMinorUnits } from '@theme/money-formatting';

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} priceDisplay - Dynamic price element inside the button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLElement} [modelChip] - Optional model chip wrapper (desktop only)
 * @property {HTMLElement} [modelLabel] - Optional model label (color · size)
 * @property {HTMLElement} [bandChip] - Optional band-upsell chip wrapper (desktop only)
 * @property {HTMLElement} [bandLabel] - Optional band count label ("+1", "+2"...)
 * @property {HTMLElement} [crossSellChip] - Optional cross-sell chip wrapper (desktop only)
 * @property {HTMLElement} [crossSellLabel] - Optional cross-sell label (selected variant titles)
 * @property {HTMLElement} [subscriptionChip] - Optional subscription chip wrapper (desktop only)
 * @property {HTMLElement} [subscriptionLabel] - Optional subscription label (selected plan title)
 */

/**
 * Sticky add-to-cart bar for the PDP. Acts as a consumer of the
 * `pdp-bundle-store` (same pattern as `<order-summary>`):
 *
 *  - `variant:update` → re-cache main variant price (cents + formatted), re-sync
 *    button price display.
 *  - `model:update` → re-read main price from dataset (morphed by Liquid),
 *    re-read model label from `<model-picker>` on the page (refs may be stale
 *    post-morph — see .claude/rules/pdp-morph.md), re-sync.
 *  - `pdp:bundle:change` → recompute total = main + sum(contributors.priceCents)
 *    and update the band-upsell chip count.
 *
 * Visibility: shows whenever the main CTA is OUT of the viewport (above OR
 * below) — important on mobile where the real CTA sits very low.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'priceDisplay', 'quantityDisplay', 'quantityNumber'];

  #selectors = {
    productForm: 'product-form-component',
    addToCartButton: '[ref="addToCartButton"]',
    cartIcon: '.header-actions__cart-icon',
    modelPickerCard: '.model-picker__card',
  };


  /** @type {string | undefined} */
  #productId;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {number} */
  #mainPriceCents = 0;

  /** @type {string} */
  #mainPriceFormatted = '';

  /** @type {string} */
  #moneyFormat = '{{amount}}';

  /** @type {string} */
  #currency = 'EUR';

  /** @type {IntersectionObserver | null} */
  #buyButtonsObserver = null;

  /** @type {IntersectionObserver | null} */
  #footerObserver = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {Element | null} */
  #observedBuyButtonsBlock = null;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #isStuck = false;

  /**
   * True between the user's click on a model-picker card and ~200ms after the
   * resulting `model:update`. While true, observer callbacks that would flip
   * `data-stuck` are ignored — preventing a visible out/in transition driven
   * by transient layout shifts during the morph.
   * @type {boolean}
   */
  #isSyncing = false;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {number | null} */
  #syncTimeout = null;

  connectedCallback() {
    super.connectedCallback();

    this.#productId = this.dataset.productId;
    this.#moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.#currency = this.dataset.currency || 'EUR';
    this.#mainPriceCents = Number(this.dataset.mainPriceCents) || 0;
    this.#mainPriceFormatted = this.refs.priceDisplay?.textContent?.trim() ?? '';

    this.#setupVisibilityObserver();

    const { signal } = this.#abortController;
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    document.addEventListener('model:update', this.#handleModelUpdate, { signal });
    document.addEventListener('pdp:bundle:change', this.#handleBundleChange, { signal });
    // Capture-phase click listener fires BEFORE model-picker's own handler,
    // letting us suppress CSS transitions on the bar before the morph begins.
    // No `model:before-update` event exists, so this is the earliest signal.
    document.addEventListener('click', this.#handleModelPickerClick, { capture: true, signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddSuccess, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddError, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    // Truncation depends on available width — re-evaluate flexible chip tooltips on resize.
    window.addEventListener('resize', this.#handleResize, { signal });
    // Escape dismisses the focus tooltip; blur re-arms it (WAI-ARIA tooltip pattern).
    this.addEventListener('keydown', this.#handleChipKeydown, { signal });
    this.addEventListener('focusout', this.#handleChipFocusOut, { signal });

    this.#getInitialQuantity();
    this.#syncFromStore(this.#bundleSnapshot());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsObserver?.disconnect();
    this.#footerObserver?.disconnect();
    this.#observedBuyButtonsBlock = null;
    this.#abortController.abort();
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    if (this.#syncTimeout) clearTimeout(this.#syncTimeout);
  }

  // ===== Visibility =====

  /**
   * Show the sticky bar whenever the main CTA is OUT of the viewport (above OR
   * below). The footer observer hides it again 200px before the page bottom.
   */
  #setupVisibilityObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // Prefer Shopify's `shopify-section-group-footer-group` over a bare
    // `<footer>` — the band-upsell-drawer also uses a `<footer>` element which
    // is found first in document order and would break the observer.
    const footer =
      document.querySelector('[class*="shopify-section-group-footer-group"]') ??
      document.querySelector('[class*="footer-group"]') ??
      document.querySelector('footer');

    this.#buyButtonsObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      if (!entry.isIntersecting && !this.#hiddenByBottom) {
        this.#showStickyBar();
      } else if (entry.isIntersecting) {
        this.#hideStickyBar();
      }
    });
    this.#buyButtonsObserver.observe(buyButtonsBlock);
    this.#observedBuyButtonsBlock = buyButtonsBlock;

    if (footer) {
      this.#footerObserver = new IntersectionObserver(
        ([entry]) => {
          if (!entry) return;
          if (entry.isIntersecting && this.#isStuck) {
            this.#hiddenByBottom = true;
            this.#hideStickyBar();
          } else if (!entry.isIntersecting && this.#hiddenByBottom) {
            this.#hiddenByBottom = false;
            const rect = buyButtonsBlock.getBoundingClientRect();
            if (rect.bottom < 0 || rect.top > window.innerHeight) {
              this.#showStickyBar();
            }
          }
        },
        { rootMargin: '200px 0px 0px 0px' }
      );
      this.#footerObserver.observe(footer);
    }

    this.#targetAddToCartButton = productForm.querySelector(this.#selectors.addToCartButton);
  }

  #showStickyBar() {
    if (this.#isSyncing) return;
    this.#isStuck = true;
    this.refs.stickyBar.dataset.stuck = 'true';
  }

  #hideStickyBar() {
    if (this.#isSyncing) return;
    this.#isStuck = false;
    this.refs.stickyBar.dataset.stuck = 'false';
  }

  // ===== Bundle store sync =====

  /**
   * @param {CustomEvent} event
   */
  #handleBundleChange = (event) => {
    const sectionId = this.dataset.sectionId ?? '';
    if (sectionId && event.detail?.sectionId !== sectionId) return;
    this.#syncFromStore(this.#bundleSnapshot());
  };

  /**
   * Bundle snapshot of this sticky's section (product-information and buy-box-v2 can share a page).
   * @returns {{ contributors: Array<{ name: string, display: any[] }>, totalCents: number }}
   */
  #bundleSnapshot() {
    return getBundleStore().snapshot(this.dataset.sectionId ?? '');
  }

  /**
   * @param {{ contributors: Array<{ name: string, display: any[] }>, totalCents: number }} snapshot
   */
  #syncFromStore(snapshot) {
    this.#syncPrice(snapshot);
    this.#syncBandChip(snapshot);
    this.#syncCrossSellChip(snapshot);
    this.#syncSubscriptionChip(snapshot);
  }

  /**
   * Update the price display: bundle total when contributors are active, main
   * variant price otherwise.
   * @param {{ contributors: Array<{ display: any[] }>, totalCents: number }} snapshot
   */
  #syncPrice(snapshot) {
    if (!this.refs.priceDisplay) return;
    const hasContribs = snapshot.contributors.some((c) => c.display.length > 0);
    if (!hasContribs) {
      this.refs.priceDisplay.textContent = this.#mainPriceFormatted;
      return;
    }
    const total = this.#mainPriceCents + snapshot.totalCents;
    this.refs.priceDisplay.textContent = formatMoney(total, this.#moneyFormat, this.#currency);
  }

  /**
   * Update the band-upsell chip count. Bands and cross-sell are tracked
   * separately because their items are conceptually different (watch straps
   * vs. extra accessories).
   * @param {{ contributors: Array<{ name: string, display: any[] }> }} snapshot
   */
  #syncBandChip(snapshot) {
    const chip = this.refs.bandChip;
    if (!chip) return;
    const bands = snapshot.contributors.find((c) => c.name === 'bands');
    const count = bands?.display.length ?? 0;
    if (count <= 0) {
      chip.hidden = true;
      return;
    }
    chip.hidden = false;
    if (this.refs.bandLabel) this.refs.bandLabel.textContent = `+${count}`;
  }

  /**
   * Update the cross-sell chip. Show the variant title when a single item is
   * selected, fall back to `+N` count when 2+ items are selected — joining
   * full titles overflows on intermediate viewports.
   * @param {{ contributors: Array<{ name: string, display: Array<{ title?: string }> }> }} snapshot
   */
  #syncCrossSellChip(snapshot) {
    const chip = this.refs.crossSellChip;
    if (!chip) return;
    const crossSell = snapshot.contributors.find((c) => c.name === 'cross-sell');
    const titles = crossSell?.display.map((d) => d.title).filter(Boolean) ?? [];
    if (titles.length === 0) {
      chip.hidden = true;
      this.#updateChipTooltip(chip);
      return;
    }
    chip.hidden = false;
    if (this.refs.crossSellLabel) {
      this.refs.crossSellLabel.textContent =
        titles.length === 1 ? (titles[0] ?? '') : `+${titles.length}`;
    }
    this.#updateChipTooltip(chip);
  }

  /**
   * Update the subscription chip. Shows when a subscription variant is selected,
   * hidden when the customer chose "No subscription" (or no contributor).
   * Prefers the plan label (e.g. "Yearly: 99,95 €/yr") for the chip value
   * since it carries both frequency and price in a single line.
   * @param {{ contributors: Array<{ name: string, display: Array<{ title?: string, subtitle?: string }> }> }} snapshot
   */
  #syncSubscriptionChip(snapshot) {
    const chip = this.refs.subscriptionChip;
    if (!chip) return;
    const subscription = snapshot.contributors.find((c) => c.name === 'subscription');
    const display = subscription?.display[0];
    if (!display) {
      chip.hidden = true;
      return;
    }
    chip.hidden = false;
    if (this.refs.subscriptionLabel) {
      this.refs.subscriptionLabel.textContent = display.subtitle ?? display.title ?? '';
    }
  }

  // ===== Chip tooltips =====

  /**
   * Re-evaluate the tooltip affordance on every flexible chip. Debounced and
   * bound to `resize` because truncation depends on the available width.
   */
  #handleResize = debounce(() => {
    for (const chip of this.querySelectorAll('.sticky-add-to-cart__chip--flexible')) {
      this.#updateChipTooltip(/** @type {HTMLElement} */ (chip));
    }
  }, 150);

  /**
   * Toggle the tooltip affordance on a flexible chip when its value is visually
   * clipped by the ellipsis. The full text always stays in the DOM (screen
   * readers read it regardless), so the tooltip and `tabindex` are sighted-user
   * aids added only when the value is actually truncated.
   * @param {HTMLElement | null | undefined} chip
   */
  #updateChipTooltip(chip) {
    if (!chip) return;

    const value = chip.querySelector('.sticky-add-to-cart__chip-value');

    if (chip.hidden || !(value instanceof HTMLElement)) {
      delete chip.dataset.truncated;
      delete chip.dataset.tipDismissed;
      chip.removeAttribute('tabindex');
      return;
    }

    if (value.scrollWidth > value.clientWidth + 1) {
      chip.dataset.tooltip = value.textContent ?? '';
      chip.dataset.truncated = 'true';
      chip.tabIndex = 0;
      return;
    }

    delete chip.dataset.tooltip;
    delete chip.dataset.truncated;
    delete chip.dataset.tipDismissed;
    chip.removeAttribute('tabindex');
  }

  /**
   * Escape hides the focus tooltip without moving focus; the dismissal is
   * cleared on blur so the next focus shows it again.
   * @param {KeyboardEvent} event
   */
  #handleChipKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const chip =
      event.target instanceof Element
        ? event.target.closest('.sticky-add-to-cart__chip--flexible[data-truncated]')
        : null;
    if (chip instanceof HTMLElement) chip.dataset.tipDismissed = 'true';
  };

  /**
   * @param {FocusEvent} event
   */
  #handleChipFocusOut = (event) => {
    const chip =
      event.target instanceof Element ? event.target.closest('.sticky-add-to-cart__chip--flexible') : null;
    if (chip instanceof HTMLElement) delete chip.dataset.tipDismissed;
  };

  // ===== Variant update =====

  /**
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    const detail = event.detail;
    if (!detail || detail.data?.productId !== this.#productId) return;

    const htmlDoc = detail.data?.html;
    const freshPrice = htmlDoc?.querySelector(
      `product-price[data-product-id="${this.#productId}"] .price__regular:not(.price__hidden) .price, product-price[data-product-id="${this.#productId}"] .price__sale:not(.price__hidden) .price-item--sale`
    );
    const freshPriceText = freshPrice?.textContent?.trim() ?? '';

    const fromResource = Number(detail.resource?.price);
    if (Number.isFinite(fromResource) && fromResource > 0) {
      this.#mainPriceCents = fromResource;
      this.#mainPriceFormatted = freshPriceText || formatMoney(fromResource, this.#moneyFormat, this.#currency);
    } else if (freshPriceText) {
      this.#mainPriceFormatted = freshPriceText;
      const parsed = convertMoneyToMinorUnits(freshPriceText, this.#currency);
      if (parsed !== null) this.#mainPriceCents = parsed;
    }

    const variant = detail.resource;
    if (variant) {
      this.dataset.variantAvailable = String(variant.available);
      this.dataset.currentVariantId = variant.id;
      const button = this.refs.addToCartButton;
      if (button) button.disabled = !variant.available;
    } else {
      this.dataset.variantAvailable = 'false';
      this.dataset.currentVariantId = '';
      const button = this.refs.addToCartButton;
      if (button) button.disabled = true;
    }

    this.#syncFromStore(this.#bundleSnapshot());
  };

  // ===== Model update (post-morph) =====

  /**
   * Re-sync internal state after the model-picker morph. The model chip's
   * label / hidden state are handled by the morph itself (see Liquid: chip is
   * always rendered with `hidden` toggled from the new product's metafields).
   * See .claude/rules/pdp-morph.md.
   */
  #handleModelUpdate = () => {
    // Re-read data attributes — the morph rewrote them with the new product's
    // values. Cached private fields (productId especially) would otherwise stay
    // pinned to the previous product, breaking #getProductForm() which filters
    // by productId.
    this.#productId = this.dataset.productId;
    this.#moneyFormat = this.dataset.moneyFormat || this.#moneyFormat;
    this.#currency = this.dataset.currency || this.#currency;
    this.#mainPriceCents = Number(this.dataset.mainPriceCents) || this.#mainPriceCents;
    this.#mainPriceFormatted = this.refs.priceDisplay?.textContent?.trim() ?? this.#mainPriceFormatted;

    // Only re-attach observers if the morph actually replaced the
    // `.buy-buttons-block`. The morph usually preserves it in place — in that
    // case our existing observers stay valid.
    if (!this.#observedBuyButtonsBlock || !document.contains(this.#observedBuyButtonsBlock)) {
      this.#buyButtonsObserver?.disconnect();
      this.#footerObserver?.disconnect();
      this.#buyButtonsObserver = null;
      this.#footerObserver = null;
      this.#observedBuyButtonsBlock = null;
      this.#hiddenByBottom = false;
      this.#setupVisibilityObserver();
    } else {
      const productForm = this.#getProductForm();
      if (productForm) {
        this.#targetAddToCartButton = productForm.querySelector(this.#selectors.addToCartButton);
      }
    }

    // The morph just rewrote `data-stuck` on the bar back to its initial
    // Liquid value (false). Restore the correct state synchronously so the
    // browser collapses the morph's stuck=false + our stuck=true into a
    // single no-op transition (initial == final value within one frame).
    this.#isSyncing = false;
    this.#evaluateVisibility();

    // Re-arm the gate to ignore tail-end observer callbacks (transient layout
    // shifts as the morph fully settles). After 200ms, clear the gate and
    // re-evaluate once more for safety.
    this.#isSyncing = true;
    if (this.#syncTimeout) clearTimeout(this.#syncTimeout);
    this.#syncTimeout = window.setTimeout(() => {
      this.#isSyncing = false;
      this.#evaluateVisibility();
      this.#syncTimeout = null;
    }, 200);

    this.#syncFromStore(this.#bundleSnapshot());
  };

  /**
   * Suppress CSS transitions on the bar before a model-picker click triggers
   * the morph. Removed in `#handleModelUpdate` after layout settles. Without
   * this, transient observer callbacks fired during the morph would visibly
   * animate the bar out/in.
   * @param {Event} event
   */
  #handleModelPickerClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest(this.#selectors.modelPickerCard)) {
      this.#isSyncing = true;
    }
  };

  /**
   * Manually re-evaluate the bar's visibility from the current geometry of the
   * observed buy-buttons block. Used after `model:update` to override stale
   * IntersectionObserver state from the morph window.
   */
  #evaluateVisibility() {
    const block = this.#observedBuyButtonsBlock;
    if (!block) return;
    const rect = block.getBoundingClientRect();
    const inViewport = rect.bottom > 0 && rect.top < window.innerHeight;
    if (inViewport) {
      this.#hideStickyBar();
    } else if (!this.#hiddenByBottom) {
      this.#showStickyBar();
    }
  }

  // ===== CTA click (puppet pattern) =====

  /**
   * Relays the click to the real add-to-cart button. The bundle store sees the
   * real button submit and runs `commit()` from there.
   * The success animation is deferred — #handleCartAddSuccess sets data-added
   * only after a CartUpdateEvent confirms the add succeeded.
   */
  handleAddToCartClick = () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.refs.addToCartButton.dataset.state = 'loading';
    this.#targetAddToCartButton.click();
  };

  // ===== Misc =====

  /**
   * Plays the success checkmark animation on the sticky bar button.
   */
  async #playSuccess() {
    this.refs.addToCartButton.removeAttribute('data-state');
    this.refs.addToCartButton.dataset.added = 'true';

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    await onAnimationEnd([this.refs.addToCartButton]);

    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  }

  /**
   * Handles a successful cart add — plays checkmark animation.
   */
  #handleCartAddSuccess = () => {
    if (this.#targetAddToCartButton) this.#targetAddToCartButton.dataset.puppet = 'false';
    if (this.refs.addToCartButton.dataset.state !== 'loading') return;
    this.#playSuccess();
  };

  /**
   * Handles a cart error — resets button state and scrolls to the error message.
   */
  #handleCartAddError = () => {
    if (this.#targetAddToCartButton) this.#targetAddToCartButton.dataset.puppet = 'false';
    if (this.refs.addToCartButton.dataset.state !== 'loading') return;
    this.refs.addToCartButton.removeAttribute('data-state');

    // Scroll to the error ribbon so the user sees the message
    const errorRibbon = document.querySelector('.product-form-text__error:not(.hidden)');
    if (errorRibbon) {
      errorRibbon.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  /**
   * @param {CustomEvent} event
   */
  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;
    this.#currentQuantity = event.detail.quantity;
    this.#updateQuantityDisplay();
  };

  /**
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    if (!this.#productId) return null;
    const sectionEl = this.closest('.shopify-section');
    if (!sectionEl) return null;
    return sectionEl.querySelector(`product-form-component[data-product-id="${this.#productId}"]`);
  }

  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateQuantityDisplay();
  }

  #updateQuantityDisplay() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;
    const available = !addToCartButton.disabled;
    quantityNumber.textContent = String(this.#currentQuantity);
    quantityDisplay.style.display = available && this.#currentQuantity > 1 ? 'inline' : 'none';
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
